import { useEffect, useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import { MouseEvent } from "react"

import { GameSettings, WordLength } from "../helpers"

type Props = {
  isOpen: boolean
  settings: GameSettings
  correctWord: string
  onClose: () => void
  onSave: (nextSettings: GameSettings) => void
}

function SettingsModal({ isOpen, settings, correctWord, onClose, onSave }: Props) {
  const [draftSettings, setDraftSettings] = useState(settings)
  const [showReveal, setShowReveal] = useState(false)

  useEffect(() => {
    setDraftSettings(settings)
    setShowReveal(false)
  }, [settings, isOpen, correctWord])

  const saveSettings = () => {
    onSave(draftSettings)
    onClose()
  }

  const setWordLength = (value: string) => {
    const parsed = Number(value) as WordLength
    setDraftSettings((previous) => ({
      ...previous,
      wordLength: parsed,
    }))
  }

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          className="settings-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="settings-modal"
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            onClick={(event: MouseEvent<HTMLDivElement>) =>
              event.stopPropagation()
            }
          >
            <h2>Settings</h2>

            <label className="settings-field" htmlFor="word-length-select">
              Word Length
              <select
                id="word-length-select"
                value={draftSettings.wordLength}
                onChange={(event) => setWordLength(event.target.value)}
              >
                <option value={4}>4</option>
                <option value={5}>5</option>
                <option value={6}>6</option>
              </select>
            </label>

            <label className="settings-field settings-toggle" htmlFor="strict-mode-toggle">
              <span>Diacritics strict mode</span>
              <input
                id="strict-mode-toggle"
                type="checkbox"
                checked={draftSettings.diacriticsStrictMode}
                onChange={(event) =>
                  setDraftSettings((previous) => ({
                    ...previous,
                    diacriticsStrictMode: event.target.checked,
                  }))
                }
              />
            </label>

            <label className="settings-field settings-toggle" htmlFor="dark-mode-toggle">
              <span>Dark mode</span>
              <input
                id="dark-mode-toggle"
                type="checkbox"
                checked={draftSettings.darkMode}
                onChange={(event) =>
                  setDraftSettings((previous) => ({
                    ...previous,
                    darkMode: event.target.checked,
                  }))
                }
              />
            </label>

            <div className="settings-actions">
              <button type="button" onClick={onClose} className="settings-secondary-button">
                Cancel
              </button>
              <button type="button" onClick={saveSettings} className="settings-primary-button">
                Save
              </button>
            </div>

            <div className="settings-reveal">
              <button
                type="button"
                className="settings-secondary-button"
                onClick={() => setShowReveal(true)}
              >
                Reveal
              </button>
              {showReveal ? (
                <p className="settings-reveal-word">
                  {correctWord || "No word available for this length"}
                </p>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export default SettingsModal
